import { useCallback, useEffect, useState } from 'react';
import { MobileHint } from './screens/MobileHint';
import { LogoMark } from './components/LogoMark';
import { BgmToggle } from './components/BgmToggle';
import { DesktopMascot } from './components/DesktopMascot';
import { TractorCursor } from './components/TractorCursor';
import { InstallPrompt } from './components/InstallPrompt';
import { reportClientError } from './telemetry';
import { Login, type LoginInitialError } from './screens/Login';
import { Admin } from './screens/Admin';
import { WishInput } from './screens/WishInput';
import { Desk } from './screens/Desk';
import { ThreeCards } from './screens/ThreeCards';
import { Timer } from './screens/Timer';
import { TraceInput } from './screens/TraceInput';
import { SegmentSummary } from './screens/SegmentSummary';
import { Reward } from './screens/Reward';
import { useAuth } from './auth/useAuth';
import { apiPost } from './api/client';
import type { DraftCard } from './types';
import {
  activePoolCards,
  applyReframedAction,
  draftToPool,
  fetchAllCards,
  loadPool,
  savePool,
  updateCardStatus,
  type PoolCard,
} from './state/cardPool';
import {
  loadSegment,
  newSegment,
  saveSegment,
  cardStatusFromUserStatus,
  type ActiveSegment,
  SEGMENT_TOTAL_ROUNDS,
} from './state/segment';

type AuthedScreen = 'bootstrap' | 'wish' | 'desk' | 'segment' | 'summary' | 'reward';

// Dev override for timer length: append ?dev_round_sec=15 to the URL to speed up rounds.
function readDevRoundMs(): number {
  if (typeof window === 'undefined') return 5 * 60 * 1000;
  const p = new URLSearchParams(window.location.search);
  const v = p.get('dev_round_sec');
  if (!v) return 5 * 60 * 1000;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 5) return 5 * 60 * 1000;
  return n * 1000;
}

const DROP_PENDING_PREFIX = 'gime5.pendingDrop.v1';

function resetLocalDevStateIfRequested(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('reset_local') !== '1') return;
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (key?.startsWith('gime5.')) localStorage.removeItem(key);
  }
  for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('gime5.')) sessionStorage.removeItem(key);
  }
  history.replaceState(null, '', '/');
}

resetLocalDevStateIfRequested();

type VerifyError = LoginInitialError;
const VERIFY_ERRORS: VerifyError[] = [
  'missing',
  'invalid',
  'expired',
  'used',
  'missing_invite',
  'invite_exhausted',
];

// PURE — must not touch history. React 18 StrictMode double-invokes useState
// initializers in dev, so any side effect here (like stripping the URL) gets
// confused: first call reads ?error=expired, strips it, returns 'expired'.
// Second call reads the already-stripped URL and returns null — wiping the error.
// We do the URL strip in a useEffect instead (runs once after both StrictMode
// passes have settled).
function readUrlError(): VerifyError | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const e = params.get('error');
  if (e && (VERIFY_ERRORS as string[]).includes(e)) {
    return e as VerifyError;
  }
  return null;
}

// Strip `error` param + collapse /login → /. Safe to call multiple times.
function cleanupAuthUrl(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const hadError = params.has('error');
  if (hadError) params.delete('error');
  const onLoginPath =
    window.location.pathname === '/login' || window.location.pathname.startsWith('/login/');
  if (hadError || onLoginPath) {
    const remaining = params.toString();
    history.replaceState(null, '', remaining ? `/?${remaining}` : '/');
  }
}

function hasPendingDrop(userId: string): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(`${DROP_PENDING_PREFIX}:${userId}`) !== null;
}

export function App() {
  // Admin console — bypasses every other concern (mobile gate, auth flow, mascot).
  // Fixed at first paint; reloading the page after `/admin` → `/` won't preserve it.
  const isAdminRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  if (isAdminRoute) return <Admin />;

  // Mobile gate detection — fixed at first paint only.
  // 2026-05-18 fix: previously this listened to matchMedia change events and would
  // remount the entire app when the user resized the browser. That unmounted WishInput
  // mid-fetch, losing both the typed wish text and the 60-150s of LLM work in flight.
  // Now we only check on initial mount. If the user resizes mid-session their layout
  // may look cramped but their data never disappears.
  const [isMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );

  const { state: authState, refresh, logout } = useAuth();

  // Anon flow state: Login is now the single entry point — it gates on email first
  // and only reveals an invite-code field if the server reports the email is new.
  // /login or ?error=xxx URLs both just land on Login with appropriate state.
  const [verifyError, setVerifyError] = useState<VerifyError | null>(() => readUrlError());
  // Strip the consumed `error` param + collapse /login → / after we've captured
  // verifyError. Effect runs once; safe under React 18 StrictMode double-invoke.
  useEffect(() => {
    cleanupAuthUrl();
  }, []);

  // Authed flow state
  // 'bootstrap' = transient state while we resolve which screen the user belongs on.
  // After auth lands we either resume a segment, or pull /api/cards to decide wish vs desk.
  const [authedScreen, setAuthedScreen] = useState<AuthedScreen>('bootstrap');
  // The current wish text (only meaningful when ThreeCards shows it from a fresh WishInput).
  // From-desk segment starts pass empty string.
  const [activeWish, setActiveWish] = useState<string>('');
  const [activeSegment, setActiveSegment] = useState<ActiveSegment | null>(null);
  const [completedSegment, setCompletedSegment] = useState<ActiveSegment | null>(null);
  const [isDeckPending, setIsDeckPending] = useState(false);
  // Once true, we've completed at least one segment in this session; Desk shows a tiny banner.
  const [recentlyCompletedSegment, setRecentlyCompletedSegment] = useState(false);
  const [canBackToDeskFromWish, setCanBackToDeskFromWish] = useState(false);

  // Resume in-progress segment OR route first-time vs returning users.
  // - in-progress segment in localStorage → resume there (highest priority)
  // - empty pool (/api/cards returns []) → 'wish' (first-time onboarding)
  // - pool has cards → 'desk' (returning user)
  useEffect(() => {
    if (authState.kind !== 'authed') return;
    let cancelled = false;
    const userId = authState.user.id || authState.user.email;
    const saved = loadSegment(userId);
    if (saved && saved.traces.length < SEGMENT_TOTAL_ROUNDS && !hasPendingDrop(userId)) {
      setActiveSegment(saved);
      setActiveWish(saved.wish);
      setAuthedScreen('segment');
      return;
    }
    // No in-progress segment → decide wish vs desk by fetching the user's card list.
    void fetchAllCards(userId).then((cards) => {
      if (cancelled) return;
      const hasAny = cards.some(
        (c) => c.status === 'pending' || c.status === 'in_progress' || c.status === 'done',
      );
      setAuthedScreen(hasAny ? 'desk' : 'wish');
    });
    return () => {
      cancelled = true;
    };
  }, [authState]);

  // Persist segment changes
  useEffect(() => {
    if (authState.kind !== 'authed') return;
    if (authedScreen === 'bootstrap') return;
    saveSegment(authState.user.id || authState.user.email, activeSegment);
  }, [activeSegment, authState, authedScreen]);

  const onCardsReady = (wish: string, cards: DraftCard[], _reasoning: string) => {
    setIsDeckPending(false);
    // Write draft cards into the local pool as 'pending' so they appear on the Desk.
    if (authState.kind === 'authed') {
      const userId = authState.user.id || authState.user.email;
      const newCards = draftToPool(cards);
      const pool = loadPool(userId);
      savePool(userId, [...newCards, ...pool]);
    }
    setActiveWish(wish);
    setCanBackToDeskFromWish(false);
    setAuthedScreen('desk');
  };

  const onDeckPendingChange = useCallback((pending: boolean) => {
    setIsDeckPending(pending);
  }, []);

  const onSelectionCommitted = (selected: PoolCard[], segmentId: string | null) => {
    const seg = newSegment(activeWish, selected);
    if (segmentId) seg.id = segmentId;
    setActiveSegment(seg);
    setRecentlyCompletedSegment(false);
    setAuthedScreen('segment');
  };

  const onLogout = async () => {
    await logout();
    setActiveWish('');
    setActiveSegment(null);
    setCompletedSegment(null);
    setIsDeckPending(false);
    setRecentlyCompletedSegment(false);
    setCanBackToDeskFromWish(false);
    setAuthedScreen('bootstrap');
  };

  // S02 fix: 「退出登录」是有摩擦的动作（下次要重新走邮件链接），过去标成「离开桌面」一点就走，
  // 极易误触。现在统一走二次确认，且只有显式确认才真退出。进行中的 segment 存在 localStorage
  // 里、退出不会被清（persist effect 在未登录态早退），所以确认文案只提示重新登录、不提丢数据。
  const requestLogout = () => {
    if (typeof window !== 'undefined' && !window.confirm('退出登录后，下次要重新用邮件链接登录。确定退出吗?')) {
      return;
    }
    void onLogout();
  };

  // 「今天到这」只是收个尾回到桌面，不应该把人登出。保留 session，重开页面还在登录态。
  const onLeaveToDesk = () => {
    setCompletedSegment(null);
    setActiveSegment(null);
    setActiveWish('');
    setRecentlyCompletedSegment(true);
    setAuthedScreen('desk');
  };

  // ------- render -------
  if (isMobile) return <MobileHint />;

  const body = (() => {
    if (authState.kind === 'loading') return <LoadingPanel />;
    if (authState.kind === 'anon') {
      // Login is the single anon entry point — it handles both new and returning
      // users (email-first; reveals an inline invite-code field only when the
      // server reports the email is new).
      const expiredNotice = authState.reason === 'expired' ? <ExpiredSessionNotice /> : null;
      return (
        <>
          {expiredNotice}
          <Login initialError={verifyError} />
        </>
      );
    }
    // authed
    if (authedScreen === 'bootstrap') return <LoadingPanel />;
    if (authedScreen === 'wish') {
      return (
        <WishInput
          user={authState.user}
          onCardsReady={onCardsReady}
          onLogout={requestLogout}
          onPendingChange={onDeckPendingChange}
          onBackToDesk={canBackToDeskFromWish ? () => setAuthedScreen('desk') : undefined}
        />
      );
    }
    if (authedScreen === 'desk') {
      return (
        <Desk
          user={authState.user}
          onStartSegment={onSelectionCommitted}
          onWriteWish={() => {
            setCanBackToDeskFromWish(true);
            setAuthedScreen('wish');
          }}
          activeSegment={activeSegment}
          onResumeSegment={() => setAuthedScreen('segment')}
          onLogout={requestLogout}
          recentlyCompletedSegment={recentlyCompletedSegment}
        />
      );
    }
    if (authedScreen === 'summary' && completedSegment) {
      return (
        <SegmentSummary
          segment={completedSegment}
          onGenerateShare={() => setAuthedScreen('reward')}
          onExit={() => {
            setCompletedSegment(null);
            setActiveSegment(null);
            setActiveWish('');
            setRecentlyCompletedSegment(true);
            setAuthedScreen('desk');
          }}
        />
      );
    }
    if (authedScreen === 'reward' && completedSegment) {
      return (
        <Reward
          segment={completedSegment}
          onContinue={() => {
            setCompletedSegment(null);
            setActiveSegment(null);
            setActiveWish('');
            setRecentlyCompletedSegment(true);
            setAuthedScreen('desk');
          }}
          onExit={onLeaveToDesk}
        />
      );
    }
    if (!activeSegment) {
      // Defensive: this branch only reachable if state was reset mid-render.
      // recovery happens via the effect below.
      return <LoadingPanel />;
    }

    const userId = authState.user.id || authState.user.email;
    const currentCard = activeSegment.cards.find((c) => c.id === activeSegment.currentCardId);

    if (activeSegment.subscreen === 'timer' && currentCard) {
      return (
        // key=round 强制每轮 remount → 计时从 5:00 重新开始。W6 同卡连开多轮时,这是防止
        // 计时不归零的保险(当前 timer/trace 互斥渲染本就会 remount,但显式标注更稳)。
        <Timer
          key={activeSegment.round}
          card={currentCard}
          durationMs={readDevRoundMs()}
          onRoundEnded={() => {
            setActiveSegment((prev) => (prev ? { ...prev, subscreen: 'trace' } : prev));
          }}
        />
      );
    }
    if (activeSegment.subscreen === 'trace' && currentCard) {
      return (
        <TraceInput
          card={currentCard}
          onActionChanged={(cardId, newAction) => {
            // Mirrors ThreeCards.onUpdateAction — applied when user reframes
            // the card inside TraceInput (via 调整策略). Updates both local pool
            // and the segment's card snapshot so subsequent rounds see new action.
            applyReframedAction(userId, cardId, newAction);
            setActiveSegment((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                cards: prev.cards.map((c) =>
                  c.id === cardId
                    ? {
                        ...c,
                        actionHistory: [...c.actionHistory, c.action],
                        action: newAction,
                      }
                    : c,
                ),
              };
            });
          }}
          onCompleted={(status, returnPoint, experience, actionUsedOverride, continueSameCard) => {
            // override 必填:用户在 TraceInput 里 reframe 后,cardPool 已更新但 React state
            // 里的 currentCard.action 还是旧值,trace 不带 override 会记错。
            const actionUsed = actionUsedOverride ?? currentCard.action;
            // Persist card status change
            updateCardStatus(userId, currentCard.id, cardStatusFromUserStatus(status));

            // Best-effort D1 write: round + experience (fire-and-forget; UI proceeds either way).
            void apiPost('/api/segment/round/end', {
              segmentId: activeSegment.id,
              cardId: currentCard.id,
              roundNo: activeSegment.round,
              actionUsed,
              returnPoint,
              userStatus: status,
              experience,
            }).catch((err) => {
              console.warn('[round/end] failed', err);
              reportClientError('write.round_end', String(err), {
                context: `segment=${activeSegment.id} round=${activeSegment.round}`,
              });
            });

            // Append trace to segment, increment round (or end).
            // IMPORTANT: also mirror the card's new status into activeSegment.cards
            // so ThreeCards' "drop completed cards" filter sees the change — segment
            // cards are a snapshot, they don't react to pool updates on their own.
            setActiveSegment((prev) => {
              if (!prev) return prev;
              const newCardStatus = cardStatusFromUserStatus(status);
              const updatedCards = prev.cards.map((c) =>
                c.id === currentCard.id ? { ...c, status: newCardStatus } : c,
              );
              const trace = {
                round: prev.round,
                cardId: currentCard.id,
                cardTitle: currentCard.title,
                actionUsed,
                returnPoint,
                userStatus: status,
                experience,
                ts: Date.now(),
              };
              const newTraces = [...prev.traces, trace];
              if (newTraces.length >= SEGMENT_TOTAL_ROUNDS) {
                // Segment finished — 8 轮上限优先于"继续做",第 8 轮 in_progress 也进 summary。
                setCompletedSegment({ ...prev, cards: updatedCards, traces: newTraces, subscreen: 'three-cards' });
                setAuthedScreen('summary');
                return null;
              }
              // W6: 「继续做」/「调整策略-接受」→ 不回工作台,直接对同一张卡再开一轮计时,
              // 把"继续"从 4 步(计时→回工作台→点卡→开始)压到 1 步。其余(完成/放一放)回工作台挑下一张。
              return {
                ...prev,
                cards: updatedCards,
                traces: newTraces,
                round: prev.round + 1,
                currentCardId: continueSameCard ? currentCard.id : null,
                subscreen: continueSameCard ? 'timer' : 'three-cards',
              };
            });
          }}
        />
      );
    }

    // Default: three-cards
    return (
      <ThreeCards
        wish={activeSegment.wish}
        cards={activeSegment.cards}
        roundNo={activeSegment.round}
        onAccept={(card) => {
          setActiveSegment((prev) =>
            prev ? { ...prev, currentCardId: card.id, subscreen: 'timer' } : prev,
          );
        }}
        onUpdateAction={(cardId, newAction) => {
          applyReframedAction(userId, cardId, newAction);
          setActiveSegment((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              cards: prev.cards.map((c) =>
                c.id === cardId
                  ? {
                      ...c,
                      actionHistory: [...c.actionHistory, c.action],
                      action: newAction,
                    }
                  : c,
              ),
            };
          });
        }}
        // "← 回桌面" — peek at the desk without losing this segment.
        // The segment stays in localStorage; user can come back any time.
        onBackToWish={() => setAuthedScreen('desk')}
        // Desk-side cards not yet in this segment, that the PlusSlot picker
        // can offer to fill an empty work-area slot.
        availableCards={activePoolCards(userId).filter(
          (pc) => !activeSegment.cards.some((sc) => sc.id === pc.id),
        )}
        onAddCard={(card) => {
          setActiveSegment((prev) => {
            if (!prev) return prev;
            if (prev.cards.some((c) => c.id === card.id)) return prev;
            return { ...prev, cards: [...prev.cards, card] };
          });
        }}
      />
    );
  })();

  const showDesktopMascot = authState.kind === 'authed' && !isDeckPending;
  const desktopMascotQuiet =
    activeSegment?.subscreen === 'timer' || activeSegment?.subscreen === 'trace';

  // When verify cookie just landed, refresh auth state.
  useEffect(() => {
    if (verifyError === null && window.location.pathname === '/') {
      // best-effort: re-poll /me after redirect to /
      const t = setTimeout(() => void refresh(), 50);
      return () => clearTimeout(t);
    }
  }, [verifyError, refresh]);

  // Recover if we ended up on 'segment' without an activeSegment (state reset edge case).
  useEffect(() => {
    if (authedScreen === 'segment' && !activeSegment) {
      setAuthedScreen('desk');
    }
  }, [authedScreen, activeSegment]);

  return (
    <main className="wrap">
      <header className="topbar">
        <div className="brand">
          <LogoMark size={36} title="每日启动器" />
          <span className="brand-text">
            <strong>每日启动器</strong>
            <em>赛博神经出品</em>
          </span>
        </div>
        <div className="topbar-right">
          <BgmToggle />
        </div>
      </header>

      {body}

      <footer className="footer">
        <span>每日启动器｜赛博神经 × CyberUnion 出品</span>
        <span className="muted">
          把“脑子很乱、开始不了”拆成今天能做的第一步。
        </span>
      </footer>

      {showDesktopMascot && <DesktopMascot quiet={desktopMascotQuiet} />}
      <TractorCursor />
      <InstallPrompt />
    </main>
  );
}

function LoadingPanel() {
  return (
    <section className="panel">
      <span className="tiny-label">
        <i className="dot cyan" /> 正在打开
      </span>
      <p className="muted">正在把桌面准备好…</p>
    </section>
  );
}

function ExpiredSessionNotice() {
  return (
    <section className="panel" style={{ marginBottom: 12 }}>
      <span className="tiny-label">
        <i className="dot amber" /> 你刚才的登录过期了
      </span>
      <p className="muted">为了安全,需要重新发一封邮件登录回到桌面。卡片和段落都还在,登录回来就接着用。</p>
    </section>
  );
}
